<template>
  <div v-if="user" class="admin-subscriptionrequest-create-view">
    <div class="breadcrump-trail">
      <router-link :to="{ name: 'admin-home' }"> Admin </router-link> /
      <router-link :to="{ name: 'admin-users-list' }"> Nutzer </router-link> /
      <router-link :to="{ name: 'admin-users-update', params: { userId } }">
        {{ userId }}
      </router-link>
      / Angebot anlegen
    </div>

    <h1>Angebot für {{ user.name }} ({{ user.email }}) erstellen</h1>

    <div class="admin-subscriptionrequest-create-view__main">
      <form
        class="column admin-subscriptionrequest-create-view__form gap-2"
        @submit.prevent="createSubscriptionRequest"
      >
        <div class="r-50-50">
          <input-dropdown
            v-model:value="plan"
            :options="orderablePlans"
            prop="label"
            label="Plan"
          />
        </div>

        <template v-if="plan?.canAddSeats">
          <hr />

          <div class="line_flex">
            <input-number
              :value="data.seats"
              :min="1"
              :label="$t('labels.licenseAmount')"
              :step="1"
              @update:value="data.seats = $event"
            />
          </div>
        </template>

        <div v-if="plan?.upgradeable">
          <h4 class="label mt-2 mb-2">{{ $t("headings.upgrades") }}</h4>
          <div class="column">
            <div v-for="upgrade of subscriptionUpgrades">
              <input-checkbox
                :value="data.upgrades.includes(upgrade.id)"
                :label="$t(`upgrades.${upgrade.id}.name`)"
                @update:value="setUpgrade(upgrade.id, $event)"
              />
            </div>
          </div>
        </div>

        <hr />

        <h4 class="mb-0 mt-1">Zeitraum</h4>

        <div class="flex gap-4 items-end">
          <input-number
            :value="data.years"
            :label="$t('roundUnits.year', 3)"
            :min="1"
            style="width: 6rem"
            @update:value="setYears($event)"
          />
          
          <input-checkbox
            v-model:value="data.renewAutomatically"
            :label="$t('labels.automaticRenewal')"
            :disabled="data.years > 1"
            class="mb-2"
          />
        </div>

        <input-text
          :value="data.startDate ?? undefined"
          label="Gültig ab"
          type="date"
          @update:value="data.startDate = $event ?? null"
        />


        <hr />

        <h4 class="mb-0 mt-1">{{ $t("headings.billingAddress") }}</h4>
        <input-text
          v-model:value="data.email"
          :label="$t('labels.email')"
          :required="true"
        />
        <address-form-fields
          v-model:firstname="data.firstname"
          v-model:lastname="data.lastname"
          v-model:company="data.company"
          v-model:vatId="data.vatId"
          v-model:street="data.street"
          v-model:supplement="data.supplement"
          v-model:zipcode="data.zipcode"
          v-model:city="data.city"
          v-model:country="data.country"
          :company-required="false"
          :show-form-errors="true"
        />

        <hr />

        <button class="button mt-4" type="submit">Angebot anlegen</button>
      </form>

      <total-costs
        v-if="plan"
        style="position: sticky; top: 1rem"
        class="admin-subscriptionrequest-create-view__costs"
        :plan="plan"
        :seats="data.seats"
        :upgrades="data.upgrades"
        :include-vat="countryIsGermany || data.vatId == null"
        :vat="0.19"
        :years="data.years"
        :per-year="data.renewAutomatically"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useAdmin } from "./useAdmin";

import InputDropdown from "@core/input/InputDropdown.vue";
import InputText from "@core/input/InputText.vue";
import InputNumber from "@core/input/InputNumber.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";
import TotalCosts from "@manager/purchasing/TotalCosts.vue";
import AddressFormFields from "@manager/common/AddressFormFields.vue";

import { usePlans, subscriptionUpgrades } from "@manager/plans/plans.store";
import { showError } from "@core/ui/alert/alert";
import { BadRequestError } from "@core/errors/Errors";

import type { AdminUser } from "@/api/User";
import type { LicensePlan } from "@manager/plans/plans.store";

const props = defineProps<{ userId?: string }>();

const router = useRouter();
const adminAPI = useAdmin();
const { orderablePlans } = usePlans();

const user = ref<AdminUser | null>(null);
const plan = ref<LicensePlan | null>(null);
const data = reactive({
  seats: 1,
  years: 1,
  upgrades: [] as string[],
  renewAutomatically: true,
  startDate: null as string | null,

  email: "",
  firstname: "",
  lastname: "",
  company: "",
  vatId: "",
  street: "",
  supplement: "",
  zipcode: "",
  city: "",
  country: "",
});

const countryIsGermany = computed(() => data.country == "DE");

const fieldErrors = ref<{ [key: string]: string }>({});

loadUser();

async function loadUser() {
  const loadedUser = await adminAPI.get<AdminUser>(`/users/${props.userId}`);
  user.value = loadedUser;

  data.email = loadedUser.email;
}

function setUpgrade(upgradeId: string, value: boolean) {
  if (value) {
    data.upgrades.push(upgradeId);
  } else {
    data.upgrades = data.upgrades.filter(id => id !== upgradeId);
  }
}

function setYears(years: number) {
  data.years = years;
  if (data.years == 1) {
    data.renewAutomatically = true;
  } else {
    data.renewAutomatically = false;
  }
}

async function createSubscriptionRequest() {
  try {
    if (plan.value == null) {
      throw new Error("Bitte Plan auswählen.");
    }

    const userId = props.userId;

    if (userId == null) {
      throw new Error("Nutzer-ID fehlt.");
    }

    const requestData: {
      user: number;
      plan: string;
      email: string;
      renewAutomatically: boolean;
      seats: number;
      years: number;
      upgrades: string[];
       
      startDate: null | string;

      billingAddress: {
        company?: string;
        vatId?: string;
        
        firstname: string;
        lastname: string;
  
        street: string;
        supplement: string;
        
        zipcode: string;
        city: string;
        country: string;
      },
    } = {
      user: parseInt(userId),
      plan: plan.value.id,

      email: data.email,

      seats: plan.value.canAddSeats ? data.seats : 1,
      years: data.years,
      upgrades: data.upgrades.slice(),
      renewAutomatically: data.renewAutomatically,

      startDate: data.startDate,
      
      billingAddress: {
        company: data.company,
        vatId: data.vatId,

        firstname: data.firstname,
        lastname: data.lastname,

        street: data.street,
        supplement: data.supplement,
        zipcode: data.zipcode,
        city: data.city,
        country: data.country
      },
    };

    await adminAPI.post(`/users/${props.userId}/subscription-requests`, { data: requestData });

    router.push({
      name: "admin-users-update",
      params: { userId: props.userId }
    });
  } catch (_error) {
    const error = _error as Error | BadRequestError;
    fieldErrors.value = "fields" in error ? error?.fields : {};

    console.log(error);
    showError(error);
  }
}
</script>

<style lang="scss">
.admin-subscriptionrequest-create-view {
  max-width: 75rem;
  width: 100%;
  padding: 2rem;

  &__main {
    display: grid;
    grid-template-columns: 42rem 1fr;
    gap: 2rem;
    align-items: start;
  }

  &__form {
    width: 100%;
    max-width: 42rem;
  }

  &__costs {
    background-color: var(--color-background-deep);
    padding: 1rem 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 0 1rem var(--color-shadow-1);
  }
}
</style>
